/**
 * Police Record Verification (PRV) — Trust & Safety card + dashboard status.
 * Spec: NEMA Police Record Verification Process Final.
 */
import { useCallback, useEffect, useState } from "react";
import api from "../../services/api";
import { payPoliceRecordVerification } from "../../services/prvPayment";
import { showToast } from "../../utils/toast";
import "./PoliceRecordVerificationCard.css";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function formatRange(from, to) {
  if (!from && !to) return "24–72 Hours";
  if (from && to) return `${formatDate(from)} – ${formatDate(to)}`;
  return formatDate(from || to);
}

export default function PoliceRecordVerificationCard({
  bookingId,
  mode = "dashboard",
  deployment = null,
  onClose = null,
  onChanged = null,
}) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [skipConfirm, setSkipConfirm] = useState(false);
  const [report, setReport] = useState(null);
  const [freshReason, setFreshReason] = useState("");

  const load = useCallback(async () => {
    if (!bookingId) return;
    setLoading(true);
    setError("");
    try {
      const res = await api.getBookingPrv(bookingId);
      setData(res || null);
    } catch (err) {
      setError(err.message || "Unable to load Police Record Verification.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    load();
  }, [load]);

  const prv = data?.prv;
  const booking = data?.booking || deployment;
  const offer = data?.offer;
  const status = String(prv?.status || "NOT_PURCHASED").toUpperCase();
  const canPurchase = ["NOT_PURCHASED", "PAYMENT_PENDING", "EXPIRED", "CANCELLED"].includes(status);
  const blockedUntilDuty = Boolean(offer?.blockedUntilDuty);
  const showOffer =
    canPurchase &&
    !blockedUntilDuty &&
    offer?.eligible !== false &&
    (mode === "deployment" || mode === "dashboard");
  const reusedAvailable = status === "COMPLETED" && prv?.stillValid && prv?.availableWithoutPurchase;
  const duplicate = data?.duplicate;

  async function handleReuse() {
    setBusy("reuse");
    setError("");
    try {
      await api.reuseBookingPrv(bookingId);
      showToast.success("Existing verification linked to this assignment.");
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.message || "Unable to reuse the existing verification.");
    } finally {
      setBusy("");
    }
  }

  async function handleFresh() {
    const reason = String(freshReason || "").trim();
    if (!reason) {
      setError("Please tell us why a fresh verification is needed.");
      return;
    }
    setBusy("fresh");
    setError("");
    try {
      await api.initiateFreshBookingPrv(bookingId, { reason });
      showToast.success("Fresh verification started. Complete payment to continue.");
      setFreshReason("");
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.message || "Unable to start a fresh verification.");
    } finally {
      setBusy("");
    }
  }

  async function handlePurchase() {
    setBusy("purchase");
    setError("");
    try {
      await api.markBookingPrvPaymentPending?.(bookingId).catch(() => null);
      const res = await payPoliceRecordVerification(bookingId);
      showToast.success(res?.message || "Police Record Verification Purchased");
      setSkipConfirm(false);
      await load();
      onChanged?.(res?.prv);
    } catch (err) {
      if (err?.code === "DUTY_DEPLOYMENT_REQUIRED") {
        setError("Confirm duty deployment on Dashboard before purchasing verification.");
      } else if (err?.code === "PAYMENT_REQUIRED" || err?.status === 402) {
        setError("Complete payment via the payment gateway to purchase verification.");
      } else {
        setError(err.message || "Unable to purchase verification.");
      }
    } finally {
      setBusy("");
    }
  }

  async function handleSkip() {
    if (!skipConfirm) {
      setSkipConfirm(true);
      return;
    }
    setBusy("skip");
    setError("");
    try {
      await api.skipBookingPrv(bookingId);
      showToast.success("Hiring continues without verification. You can purchase later from your dashboard.");
      setSkipConfirm(false);
      await load();
      onChanged?.();
      onClose?.();
    } catch (err) {
      if (err?.code === "DUTY_DEPLOYMENT_REQUIRED") {
        setError("Confirm duty deployment on Dashboard before skipping verification.");
      } else {
        setError(err.message || "Unable to skip verification.");
      }
    } finally {
      setBusy("");
    }
  }

  async function handleViewReport() {
    setBusy("report");
    setError("");
    try {
      const res = await api.getBookingPrvReport(bookingId);
      setReport(res?.report || res);
      await api.markBookingPrvReportViewed?.(bookingId).catch(() => null);
      await load();
    } catch (err) {
      setError(err.message || "Unable to load verification report.");
    } finally {
      setBusy("");
    }
  }

  if (!bookingId) return null;

  if (loading) {
    return (
      <section className={`prv-card prv-card--${mode}`}>
        <p className="prv-card__note">Loading Police Record Verification…</p>
      </section>
    );
  }

  return (
    <section className={`prv-card prv-card--${mode} prv-card--${(prv?.display?.tone || "muted").toLowerCase()}`}>
      <header className="prv-card__head">
        <div>
          <p className="prv-card__eyebrow">Trust &amp; Safety</p>
          <h3 className="prv-card__title">Police Record Verification</h3>
        </div>
        {onClose && mode === "deployment" ? (
          <button type="button" className="prv-card__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        ) : null}
      </header>

      {mode === "deployment" && booking ? (
        <div className="prv-card__deployment">
          <p>
            <strong>Caregiver:</strong> {booking.caregiverName || "—"}
          </p>
          {booking.date ? (
            <p>
              <strong>Deployment date:</strong> {formatDate(booking.date)}
              {booking.startTime ? ` · ${booking.startTime}` : ""}
            </p>
          ) : null}
          {booking.serviceAddress ? (
            <p>
              <strong>Service address:</strong> {booking.serviceAddress}
            </p>
          ) : null}
        </div>
      ) : null}

      <p className="prv-card__lead">
        {prv?.trustCopy ||
          "Increase your peace of mind by verifying the selected caregiver through our legal verification partner."}
      </p>

      {error ? <p className="prv-card__error">{error}</p> : null}

      {blockedUntilDuty ? (
        <p className="prv-card__note">
          Confirm the service address and e-agreement on Dashboard first. Police Record Verification is offered after
          duty deployment for 12h / 24h assignments.
        </p>
      ) : null}

      {offer && offer.eligible === false && !blockedUntilDuty && canPurchase ? (
        <p className="prv-card__note">
          Police Record Verification is offered after duty deployment for 12 hour and 24 hour live-in assignments.
        </p>
      ) : null}

      {status === "COMPLETED" && prv?.stillValid ? (
        <div className="prv-card__status-block">
          <p className="prv-card__status-label">🟢 {prv.display?.label || "Police Record Verified"}</p>
          <ul className="prv-card__meta">
            <li>
              <span>Completed</span>
              <strong>{formatDate(prv.verificationCompletedAt)}</strong>
            </li>
            <li>
              <span>Verification ID</span>
              <strong>{prv.verificationId || "—"}</strong>
            </li>
            <li>
              <span>Valid until</span>
              <strong>{formatDate(prv.verificationValidUntil)}</strong>
            </li>
          </ul>
          {reusedAvailable || prv.stillValid ? (
            <p className="prv-card__note">Police Record Verification Available — no new purchase required.</p>
          ) : null}
          <button type="button" className="prv-card__btn prv-card__btn--primary" disabled={busy === "report"} onClick={handleViewReport}>
            {busy === "report" ? "Opening…" : "View Verification Report"}
          </button>
        </div>
      ) : null}

      {status === "FAILED" ? (
        <div className="prv-card__status-block">
          <p className="prv-card__status-label">🔴 {prv.display?.label}</p>
          <p className="prv-card__note">{prv.display?.message}</p>
        </div>
      ) : null}

      {["INITIATED", "UNDER_VERIFICATION", "PAYMENT_PENDING"].includes(status) ? (
        <div className="prv-card__status-block">
          <p className="prv-card__status-label">
            🟡 {status === "PAYMENT_PENDING" ? "Payment pending" : "In-Progress"}
            {prv.display?.label && prv.display.label !== "In-Progress" ? ` · ${prv.display.label}` : ""}
          </p>
          <ul className="prv-card__meta">
            {prv.verificationRequestedAt ? (
              <li>
                <span>Started</span>
                <strong>{formatDate(prv.verificationRequestedAt)}</strong>
              </li>
            ) : null}
            <li>
              <span>Estimated completion</span>
              <strong>{formatRange(prv.estimatedCompletionFrom, prv.estimatedCompletionTo)}</strong>
            </li>
            {prv.verificationId ? (
              <li>
                <span>Verification ID</span>
                <strong>{prv.verificationId}</strong>
              </li>
            ) : null}
          </ul>
          <ol className="prv-card__progress">
            {(prv.progress || []).map((step) => (
              <li
                key={step.key}
                className={`prv-card__progress-step${step.done ? " is-done" : ""}${step.current ? " is-current" : ""}${
                  step.failed ? " is-failed" : ""
                }`}
              >
                {step.label}
              </li>
            ))}
          </ol>
          <p className="prv-card__note">{prv.noDelayCopy}</p>
          {status === "PAYMENT_PENDING" ? (
            <button type="button" className="prv-card__btn prv-card__btn--primary" disabled={!!busy} onClick={handlePurchase}>
              {busy === "purchase" ? "Processing…" : `Purchase Police Verification – ₹${offer?.feeInr || prv?.feeInr || 500} + 18% GST`}
            </button>
          ) : null}
        </div>
      ) : null}

      {duplicate?.exists ? (
        <div className="prv-card__status-block">
          <p className="prv-card__status-label">{duplicate.message}</p>
          <ul className="prv-card__meta">
            <li>
              <span>PRV reference</span>
              <strong>{duplicate.verificationId || "—"}</strong>
            </li>
            <li>
              <span>Verified on</span>
              <strong>{formatDate(duplicate.verificationDate)}</strong>
            </li>
            <li>
              <span>Valid until</span>
              <strong>{formatDate(duplicate.validUntil)}</strong>
            </li>
          </ul>
          <div className="prv-card__actions">
            <button type="button" className="prv-card__btn prv-card__btn--primary" disabled={!!busy} onClick={handleViewReport}>
              View existing report
            </button>
            <button type="button" className="prv-card__btn" disabled={!!busy} onClick={handleReuse}>
              {busy === "reuse" ? "Linking…" : "Reuse existing verification"}
            </button>
            <button type="button" className="prv-card__btn" disabled={!!busy} onClick={handleSkip}>
              {busy === "skip" ? "Cancelling…" : "Cancel new purchase"}
            </button>
          </div>
          <label className="prv-card__note">
            Start a fresh verification (reason required)
            <input value={freshReason} onChange={(e) => setFreshReason(e.target.value)} placeholder="Why a new check is needed" />
          </label>
          <button type="button" className="prv-card__btn" disabled={!!busy} onClick={handleFresh}>
            {busy === "fresh" ? "Starting…" : "Initiate fresh verification"}
          </button>
        </div>
      ) : null}
      {showOffer && canPurchase && status !== "COMPLETED" && !duplicate?.exists ? (
        <div className="prv-card__offer">
          <p className="prv-card__status-label">
            {status === "EXPIRED" ? "Verification Expired" : "Police Verification – Not Purchased"}
          </p>
          <ul className="prv-card__includes">
            {(prv?.includes || []).map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <div className="prv-card__fee-row">
            <span>Time required</span>
            <strong>{offer?.timeRequired || "24–72 Hours"}</strong>
          </div>
          <div className="prv-card__fee-row">
            <span>Fee</span>
            <strong>₹{offer?.feeInr || 500} + 18% GST (One Time)</strong>
          </div>
          <p className="prv-card__note">{prv?.noDelayCopy}</p>

          {skipConfirm ? (
            <div className="prv-card__skip-confirm">
              <p>
                Police Record Verification is optional. You may purchase verification later from your dashboard while
                the caregiver assignment is active.
              </p>
              <div className="prv-card__actions">
                <button type="button" className="prv-card__btn prv-card__btn--primary" disabled={!!busy} onClick={handleSkip}>
                  {busy === "skip" ? "Continuing…" : "Proceed"}
                </button>
                <button type="button" className="prv-card__btn" disabled={!!busy} onClick={() => setSkipConfirm(false)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="prv-card__actions">
              <button type="button" className="prv-card__btn prv-card__btn--primary" disabled={!!busy} onClick={handlePurchase}>
                {busy === "purchase"
                  ? "Processing…"
                  : mode === "deployment"
                    ? `Verify Caregiver – ₹${offer?.feeInr || 500} + 18% GST`
                    : `Verify Now ₹${offer?.feeInr || 500} + 18% GST`}
              </button>
              {mode === "deployment" ? (
                <button type="button" className="prv-card__btn" disabled={!!busy} onClick={handleSkip}>
                  Continue Without Verification
                </button>
              ) : null}
            </div>
          )}
        </div>
      ) : null}

      {report ? (
        <div className="prv-card__report" role="status">
          <h4>Verification Report</h4>
          <p>
            <strong>Outcome:</strong> {report.outcome || "VERIFIED"}
          </p>
          <p>
            <strong>Verification ID:</strong> {report.verificationId || "—"}
          </p>
          <p>
            <strong>Completed:</strong> {formatDate(report.completedAt)}
          </p>
          <p>
            <strong>Valid until:</strong> {formatDate(report.validUntil)}
          </p>
          <p className="prv-card__note">{report.summary}</p>
        </div>
      ) : null}
    </section>
  );
}
